import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import type { Session } from '../../api/session';
import { getObject, getRequestsPage, type ObjectInfo } from '../../api/tenant';
import { RequestType, describeRequest, groupRequests } from '../../domain/requests';
import { Chips } from '../../ui/Choice';
import { Skeleton } from '../../ui/Skeleton';
import { LoadFailed } from '../common/LoadFailed';
import p from '../passes/passes.module.css';
import s from './requests.module.css';

const TYPES = [
  { id: 'all' as const, label: 'Все' },
  { id: RequestType.Message, label: 'Сообщения' },
  { id: RequestType.Pass, label: 'Пропуска' },
  { id: RequestType.Confidance, label: 'Доверенности' },
  { id: RequestType.Join, label: 'Присоединение' },
];

/** «Обращения»: grouped by status, filter by type (spec §6.7). */
export function RequestsScreen({ session, object }: { session: Session; object: ObjectInfo }) {
  const [type, setType] = useState<number | 'all'>('all');
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const requests = useInfiniteQuery({
    queryKey: ['requests', object.id, type],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getRequestsPage(session, { objectId: object.id, type: type === 'all' ? undefined : type, page: pageParam }),
    getNextPageParam: (last) => last.nextPage ?? undefined,
  });
  const items = (requests.data?.pages ?? []).flatMap((pg) => pg.items);
  const canWrite = details.data?.settings.enableRequestsForMessagesToMC === true;

  return (
    <div className={p.page}>
      <div className={s.titleRow}>
        <h1 className={p.title}>Обращения</h1>
        {canWrite && (
          <Link to="/requests/new" className={s.write}>
            Написать в УК
          </Link>
        )}
      </div>
      <Chips label="Тип обращения" value={type} items={TYPES} onChange={(v) => setType(v ?? 'all')} />

      {requests.isPending ? (
        <Skeleton height={180} />
      ) : requests.isError ? (
        <LoadFailed error={requests.error} onRetry={() => void requests.refetch()} />
      ) : items.length === 0 ? (
        <p className={p.empty}>Обращений нет</p>
      ) : (
        groupRequests(items).map((g) => (
          <section key={g.label} className={s.group}>
            <h2 className={s.groupTitle}>{g.label}</h2>
            <ul className={p.list} aria-label={g.label}>
              {g.items.map((r) => {
                const d = describeRequest(r);
                return (
                  <li key={r.id}>
                    <Link to={`/requests/${r.id}`} className={s.row}>
                      <span className={s.kind}>{d.kind}</span>
                      <span className={s.name}>{d.title}</span>
                      {d.subtitle && <span className={s.sub}>{d.subtitle}</span>}
                      {d.reason && <span className={s.reason}>{d.reason}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
      {requests.hasNextPage && (
        <button type="button" className={p.more} onClick={() => void requests.fetchNextPage()} disabled={requests.isFetchingNextPage}>
          {requests.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
        </button>
      )}
    </div>
  );
}
